'use client';

import { useEffect, useRef } from "react";
import Link from "next/link";
import { siteConfig } from "@/lib/siteConfig";
import {
  Plus, Headset, Mail, Phone, PhoneCall, MessagesSquare,
  MessageSquareText, UserRoundCheck, ClipboardCheck,
} from "lucide-react";
import GlobalTitle from "@shared/GlobalTitle";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import MailtoLink from "@shared/MailtoLink";
const ANIM_STYLES = `
  @keyframes slideUp {
    from { opacity: 0; transform: translateY(24px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .faq-slide-up { animation: slideUp 0.7s cubic-bezier(.22,1,.36,1) both; }
  @media (prefers-reduced-motion: reduce) {
    .faq-slide-up { animation: none; }
  }
  .faq-help-panel[data-fit="1"] .faq-help-desc,
  .faq-help-panel[data-fit="2"] .faq-help-desc,
  .faq-help-panel[data-fit="3"] .faq-help-desc,
  .faq-help-panel[data-fit="2"] .faq-help-lead,
  .faq-help-panel[data-fit="3"] .faq-help-lead,
  .faq-help-panel[data-fit="3"] .faq-help-pill { display: none; }
`;
const FIT_LEVELS = 3;

const HELP_STEPS = [
  { icon: MessageSquareText, title: "Share Your Requirements", desc: "" },
  { icon: UserRoundCheck, title: "Free Discovery Call", desc: "" },
  { icon: ClipboardCheck, title: "Get a Tailored Estimate", desc: "" },
];

function fitPanel(el) {
  for (let level = 0; level <= FIT_LEVELS; level++) {
    el.dataset.fit = String(level);
    if (el.scrollHeight <= el.clientHeight + 1) return;
  }
}

function FaqHelpPanel({ title, coloredText, text }) {
  const panelRef = useRef(null);

  useEffect(() => {
    const el = panelRef.current;
    if (!el) return;
    const fit = () => fitPanel(el);
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    for (const child of el.children) ro.observe(child);
    el.addEventListener("animationend", fit);
    return () => {
      ro.disconnect();
      el.removeEventListener("animationend", fit);
    };
  }, []);

  return (
    <div
      ref={panelRef}
      className="faq-help-panel flex-1 min-h-0 flex flex-col justify-between gap-3 overflow-hidden text-white"
    >
      {/* Intro */}
      <div className="faq-slide-up shrink-0">
        <span className="faq-help-pill mb-3 inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/15 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-white/85">
          <MessagesSquare size={13} className="text-primary" />
          Still Have Questions ?
        </span>
        <h3 className="font-bold text-2xl xl:text-[1.75rem] leading-tight">
          {title} <span className="text-primary">{coloredText}</span>
        </h3>
        <p className="faq-help-lead mt-1.5 text-sm sm:text-14 text-white/75">
          {text}
        </p>
      </div>

      {/* How it works */}
      <ol
        aria-label="How our free consultation works"
        className="faq-slide-up shrink-0 grid grid-cols-1 sm:grid-cols-3 gap-3"
        style={{ animationDelay: "0.1s" }}
      >
        {HELP_STEPS.map(({ icon: Icon, title: stepTitle, desc }) => (
          <li key={stepTitle} className="group rounded-xl bg-white/5 border border-white/10 p-3 transition-colors duration-300 hover:bg-white/10 hover:border-primary/40">
            <div className="flex items-center gap-2.5">
              <span aria-hidden="true" className="w-8 h-8 rounded-lg bg-primary/15 text-primary flex items-center justify-center shrink-0 transition-colors duration-300 group-hover:bg-primary group-hover:text-white">
                <Icon size={16} />
              </span>
              <h4 className="text-sm font-semibold leading-snug">{stepTitle}</h4>
            </div>
            <p className="faq-help-desc mt-2 text-xs text-white/65">{desc}</p>
          </li>
        ))}
      </ol>
    </div>
  );
}

function buildFaqJsonLd(items) {
  const entries = items.filter((item) => typeof item.answer === "string");
  if (!entries.length) return null;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: entries.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };

  return JSON.stringify(jsonLd).replace(/</g, "\\u003c");
}

export default function FaqSection({
  topText = "Frequently Asked Questions",
  titleText = "Find What You Were",
  coloredText = "Looking For",
  email = siteConfig.email,
  phone = siteConfig.phone,
  items = [],
  bgClass = "bg-background",
  callbackHref = "/contact-us",
  helpTitle = "Get Expert Answers for",
  helpColoredText = "Your Project",
  helpText = "",
}) {
  const faqJsonLd = buildFaqJsonLd(items);

  return (
    <section className={`py-12 md:py-20 ${bgClass}`}>
      {faqJsonLd && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: faqJsonLd }}
        />
      )}
      <style>{ANIM_STYLES}</style>
      <div className="max-w-[1440px] mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div className="flex flex-col gap-6">
            <GlobalTitle
              topText={topText}
              titleText={titleText}
              coloredText={coloredText}
              classes="text-left !mb-0"
            />
            <div className="relative flex-1 lg:min-h-92">
              <div className="relative isolate rounded-[28px] overflow-hidden flex flex-col gap-5 p-5 sm:p-8 bg-ink border border-white/10 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.5),inset_0_1px_0_rgba(255,255,255,0.08)] lg:absolute lg:inset-0">
                <div aria-hidden="true" className="absolute inset-0 -z-10 pointer-events-none select-none">
                  <div className="absolute -top-60 left-[-519px] w-[900px] h-[400px] bg-primary/[0.08] rounded-full blur-[130px]" />
                  <div className="absolute inset-0 bg-white/4" />
                  <div className="absolute -top-6 -right-6 w-24 h-24 border-2 border-primary/20 rotate-10 rounded-2xl" />
                  <div className="absolute -bottom-5 left-2 w-16 h-16 rounded-xl -rotate-10 bg-primary/10" />
                </div>

                <FaqHelpPanel title={helpTitle} coloredText={helpColoredText} text={helpText} />

                {/* Contact card */}
                <div className="faq-slide-up relative mt-auto shrink-0" style={{ animationDelay: "0.2s" }}>
                  <div className="flex flex-row gap-3 items-center justify-between rounded-2xl bg-white/10 border border-white/15 p-4 sm:p-5 text-white">
                    <div className="flex flex-row gap-3 items-center min-w-0">
                      <div aria-hidden="true" className="w-10 h-10 rounded-full bg-primary flex items-center justify-center shrink-0">
                        <Headset size={18} />
                      </div>
                      <div className="mb-0 min-w-0">
                        <p className="font-bold text-lg sm:text-xl leading-tight">
                          Speak With Our Experts Today
                        </p>
                        <div className="flex flex-row flex-wrap gap-x-6 gap-y-1.5 text-sm mt-2 text-white/90">
                          <MailtoLink email={email} title="Email Our Team" className="flex items-center gap-2 hover:text-primary transition-colors">
                            <Mail size={14} className="shrink-0" aria-hidden="true" />
                            <span suppressHydrationWarning>{email}</span>
                          </MailtoLink>
                          <a href={`tel:${phone.replace(/\s/g, "")}`} title="Call Our Team" className="flex items-center gap-2 hover:text-primary transition-colors">
                            <Phone size={14} className="shrink-0" aria-hidden="true" />
                            <span suppressHydrationWarning>{phone}</span>
                          </a>
                        </div>
                      </div>
                    </div>
                    <div className="relative group shrink-0">
                      <span className="absolute inset-0 rounded-full bg-primary/70 motion-safe:animate-ping pointer-events-none" />
                      <Link
                        href={callbackHref}
                        aria-label="Request a free callback"
                        scroll={!callbackHref.startsWith("#")}
                        onClick={(e) => {
                          if (!callbackHref.startsWith("#")) return;
                          e.preventDefault();
                          document
                            .getElementById(callbackHref.slice(1))
                            ?.scrollIntoView({ behavior: "smooth" });
                        }}
                        className="relative w-10 h-10 rounded-full bg-primary flex items-center justify-center shadow-glow-sm hover:shadow-glow-red hover:bg-primary/90 hover:scale-110 active:scale-95 transition-all duration-300 cursor-pointer"
                      >
                        <PhoneCall size={18} />
                      </Link>
                      <div className="absolute -top-2 left-1/2 -translate-x-1/2 -translate-y-full whitespace-nowrap rounded-lg bg-ink px-2.5 py-1.5 text-xs font-medium text-white opacity-0 scale-95 pointer-events-none transition-all duration-200 group-hover:opacity-100 group-hover:scale-100 shadow-lg">
                        Request a Callback
                        <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-x-4 border-x-transparent border-t-4 border-t-ink" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Red base bar, as under the form card */}
              <div aria-hidden="true" className="absolute -bottom-1.5 left-7 right-7 h-1.5 rounded-b-xl bg-primary" />
            </div>
          </div>
          <Accordion
            type="single"
            collapsible
            className="flex flex-col gap-4 lg:justify-end"
          >
            {items.map((item) => (
              <AccordionItem
                key={item.id}
                value={item.id}
                className="bg-card rounded-2xl border border-border data-[state=open]:border-primary/30 overflow-hidden not-last:border-b transition-colors duration-200"
              >
                <AccordionTrigger className="py-3 px-4 items-center hover:no-underline cursor-pointer **:data-[slot=accordion-trigger-icon]:hidden">
                  <span className="flex-1 text-left font-bold text-ink text-base sm:text-lg">
                    {item.question}
                  </span>
                  <span className="ml-4 shrink-0 w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center transition-colors duration-200 group-aria-expanded/accordion-trigger:bg-red-gradient">
                    <Plus size={20} className="text-primary transition-transform duration-300 group-aria-expanded/accordion-trigger:rotate-45 group-aria-expanded/accordion-trigger:text-white" />
                  </span>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6 pt-0 text-muted-foreground leading-normal">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}
